import {
  useETANotificationContext,
  SendNotificationType,
  SendNotificationOption
} from "../ETANotificationConfigsProvider";
import styled from "@emotion/styled";
import {Controller, useWatch} from "react-hook-form";
import {Radio, RadioGroup} from "@blueprintjs/core";
import {COLORS, Constants} from "../../../../../constants/constants";
import {ETAInput} from "../common/ETANotificationConfigs.style";
import {PremiumUpgradePopover} from "../common/PremiumUpgradePopover";
import {ETAInputWrapperWithErrorMessage} from "./ETAInputWrapperWithErrorMessage";
import {handleNumberKeyDown, handleNumberPaste} from "../common/ETANotificationConfigs.utils";
import {PremiumStarIcon} from "../../../../common/icons";
import {useFeatureFlagContext} from "../../../../../providers/FeatureFlagProvider";
import {useContext, useMemo} from "react";
import {AuthContext} from "../../../../../providers/AuthProvider";

export const ETANotificationSettings = () => {
  const {etaNotificationPremium} = useFeatureFlagContext();
  const {isEcourierUser, isSuperUser} = useContext(AuthContext);

  // Internal e-Courier admins bypass the premium UI lock; external/customer admins remain gated.
  const isInternalSuperAdmin = useMemo(() => {
    return Boolean(isEcourierUser && isSuperUser);
  }, [isEcourierUser, isSuperUser]);
  const canEditPremium = etaNotificationPremium || isInternalSuperAdmin;
  const {etaFormState} = useETANotificationContext();
  const {control, setValue, clearErrors} = etaFormState;

  const notificationType = useWatch({
    control,
    name: "sendNotificationVia.type"
  });

  const renderSwitchBtn = ({field}: {field: any}) => {
    const handleChange = (option: SendNotificationType) => {
      field.onChange(option);
      // Reset notification option and value when type changes
      setValue("sendNotificationVia.value", "", {shouldValidate: false});
      clearErrors("sendNotificationVia.value");
    };
    return (
      <div style={{display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "12px"}}>
        <ETASwitchContainer>
          <ETASwitchButton
            type="button"
            selected={field.value === SendNotificationType.EMAIL}
            onClick={() => handleChange(SendNotificationType.EMAIL)}
          >
            Email
          </ETASwitchButton>
          <ETASwitchButton
            type="button"
            selected={field.value === SendNotificationType.TEXT}
            onClick={() => handleChange(SendNotificationType.TEXT)}
            disabled={!canEditPremium}
          >
            Text
            {!canEditPremium && (
              <span style={{transform: "translate(50px,2px)"}}>
                <PremiumStarIcon />
              </span>
            )}
          </ETASwitchButton>
        </ETASwitchContainer>
        {!canEditPremium && <PremiumUpgradePopover />}
      </div>
    );
  };

  const renderNotiOption = ({field}: {field: any}) => {
    const suffixLabel = notificationType === SendNotificationType.EMAIL ? "Email" : "Phone";
    const disabledCustomField = field.value !== SendNotificationOption.CUSTOM;
    // Text is a premium feature — when the config uses Text and the user can't edit premium
    // (external/non-premium), the recipient options (stop/caller/custom) are read-only too.
    const optionsLocked = notificationType === SendNotificationType.TEXT && !canEditPremium;

    const handleChangeOption = (e: any) => {
      setValue("sendNotificationVia.value", "", {shouldValidate: false});
      clearErrors("sendNotificationVia.value");
      field.onChange(e);
    };

    return (
      <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
        <ETARadioGroup onChange={handleChangeOption} selectedValue={field.value} inline={true} disabled={optionsLocked}>
          <Radio label={`Stop ${suffixLabel}`} value={SendNotificationOption.STOP} />
          <Radio label={`Caller ${suffixLabel}`} value={SendNotificationOption.CALLER} />
          <Radio label="Custom" value={SendNotificationOption.CUSTOM} />
        </ETARadioGroup>
        <Controller
          render={({field: customField, fieldState}) => (
            <ETAInputWrapperWithErrorMessage errorMessage={fieldState.error?.message}>
              <ETAInput
                type={notificationType === SendNotificationType.EMAIL ? "text" : "number"}
                value={customField.value}
                onChange={(e) => {
                  customField.onChange(e);
                }}
                onKeyDown={(e) => {
                  if (notificationType === SendNotificationType.TEXT) {
                    handleNumberKeyDown(e);
                  }
                }}
                onPaste={(e) => {
                  if (notificationType === SendNotificationType.TEXT) {
                    handleNumberPaste(customField.onChange)(e);
                  }
                }}
                disabled={disabledCustomField || optionsLocked}
                width={"224px"}
              />
            </ETAInputWrapperWithErrorMessage>
          )}
          name={"sendNotificationVia.value"}
          control={control}
          rules={{
            required: {
              value: !disabledCustomField,
              message: "This field is required."
            },
            validate: (value) => {
              // Skip validation if field is disabled
              if (disabledCustomField) {
                return true;
              }

              if (notificationType === SendNotificationType.EMAIL && !Constants.REGEX.EMAIL_REGEX.test(value)) {
                return "Please enter a valid email address.";
              } else if (notificationType === SendNotificationType.TEXT && value.trim().length !== 10) {
                return "Please enter a valid 10-digit phone number.";
              }
              return true;
            }
          }}
        />
      </div>
    );
  };

  return (
    <ETANotificationSettingsContainer>
      <Controller name="sendNotificationVia.type" control={control} render={renderSwitchBtn} />
      <Controller name="sendNotificationVia.option" control={control} render={renderNotiOption} />
    </ETANotificationSettingsContainer>
  );
};

const ETANotificationSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ETASwitchContainer = styled.div`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 4px;
  display: flex;
  flex-direction: row;
  width: fit-content;
  gap: 4px;
`;

const ETASwitchButton = styled.button<{selected: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 58px;
  background: ${(props) => (props.selected ? "#2563EB" : "#fff")};
  color: ${(props) => (props.selected ? "#fff" : "#6B7280")};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;

  &:disabled {
    /* Selected + disabled (e.g. external admin viewing a saved Text config): a lighter
       blue than the active state. Unselected + disabled: stays neutral (not a blue fill)
       so it doesn't compete with the selected option, with a readable label. */
    background: ${(props) => (props.selected ? "#dbeafe" : "#fff")};
    color: ${(props) => (props.selected ? "#2563eb" : "#6b7280")} !important;
    cursor: not-allowed;
  }
`;

const ETARadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  width: fit-content;
  justify-content: flex-start;
  gap: 32px;

  .bp5-control input:checked ~ .bp5-control-indicator {
    background-color: ${COLORS.DARK_BLUE} !important;
  }
  .bp5-control input:checked ~ span {
    color: ${COLORS.TEXT_BLACK} !important;
  }

  .bp5-radio {
    height: 16px;
    margin: 0;
  }
`;
